import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useToast } from '../components/Toast';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MonthEntry {
  monthNum: number;
  monthName: string;
  forecast: number;
  actual: number;
}

interface QuarterSummary {
  quarterLabel: string;
  months: MonthEntry[];
  totalForecast: number;
  totalActual: number;
  tracker: number;
  bonusEarned: boolean;
  bonusAmount: number;
}

interface IncentiveMonthlyData {
  employeeUpn: string;
  displayName: string;
  year: number;
  quarters: QuarterSummary[];
  annualTarget: number;
  actualsBilled: number;
  annualTracker: number;
  quarterlyBonusTotal: number;
  annualBonus: number;
  annualBonusGross?: number;
  annualBonusTax?: number | null;
  annualBonusNet?: number | null;
  annualBonusEarned?: boolean;
  annualBonusSource?: string;
  kpiBonus: number;
  totalIncentive: number;
  // Finance Hub personal figures (Nicole & Wynand, source=person, rollup:incl_equity): hub | db-fallback | manual
  financeSource?: 'hub' | 'db-fallback' | 'manual';
  hubLinked?: boolean;
  hubEntity?: string | null;
  hubAsOf?: string | null;
  hubError?: string | null;
  hubErrorCode?: string | null;
}

interface MonthlyFormState {
  employeeUpn: string;
  year: number;
  months: Record<number, { forecast: number; actual: number }>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const QUARTER_CONFIG = [
  { key: 'Q1', label: 'Q1 (Mar-May)', months: [3, 4, 5] },
  { key: 'Q2', label: 'Q2 (Jun-Aug)', months: [6, 7, 8] },
  { key: 'Q3', label: 'Q3 (Sep-Nov)', months: [9, 10, 11] },
  { key: 'Q4', label: 'Q4 (Dec-Feb)', months: [12, 1, 2] },
];

const MONTH_NAMES: Record<number, string> = {
  1: 'January', 2: 'February', 3: 'March', 4: 'April',
  5: 'May', 6: 'June', 7: 'July', 8: 'August',
  9: 'September', 10: 'October', 11: 'November', 12: 'December',
};

const QUARTERLY_BONUS = 5000;

const currencyFormat = (val: number) =>
  `R ${val.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatAsOf = (iso?: string | null) => {
  if (!iso) return 'latest batch';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Africa/Johannesburg' });
};

const initMonths = (): Record<number, { forecast: number; actual: number }> => {
  const m: Record<number, { forecast: number; actual: number }> = {};
  for (let i = 1; i <= 12; i++) m[i] = { forecast: 0, actual: 0 };
  return m;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const IncentiveManagementPage: React.FC = () => {
  const api = useApi();
  const { user, getHubToken, requestHubConsent } = useAuth();
  const [hubNeedsConsent, setHubNeedsConsent] = useState(false);

  /** Hub-scoped token for the Nicole/Wynand Finance Hub link (silent only; never blocks the page). */
  const hubHeaders = async (): Promise<Record<string, string> | undefined> => {
    try {
      const hub = await getHubToken();
      setHubNeedsConsent(Boolean(hub.needsConsent));
      return hub.token ? { 'X-Hub-Authorization': `Bearer ${hub.token}` } : undefined;
    } catch {
      return undefined;
    }
  };
  const { showToast } = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [editingUpn, setEditingUpn] = useState<string | null>(null);
  const [editingRecord, setEditingRecord] = useState<IncentiveMonthlyData | null>(null);
  const [formData, setFormData] = useState<MonthlyFormState>({ employeeUpn: '', year: 2026, months: initMonths() });

  // Fetch monthly data
  const { data: records, isLoading, error, refetch } = useQuery({
    queryKey: ['incentive-monthly', selectedYear],
    queryFn: async () => {
      const data = await api.get(`/incentives/monthly?year=${selectedYear}`, await hubHeaders());
      return data as IncentiveMonthlyData[];
    },
    retry: 2,
    staleTime: 30000,
  });

  // Fetch employee list for dropdown (new record only)
  const { data: employees } = useQuery({
    queryKey: ['employees-list'],
    queryFn: async () => {
      const data = await api.get('/employees');
      return data as Array<{ EmployeeId: number; Upn: string; DisplayName: string }>;
    },
  });

  const filteredRecords = useMemo(() => {
    if (!records) return [];
    if (!searchQuery.trim()) return records;
    const q = searchQuery.toLowerCase();
    return records.filter(r =>
      r.displayName.toLowerCase().includes(q) || r.employeeUpn.toLowerCase().includes(q),
    );
  }, [records, searchQuery]);

  // Form computed values
  const computed = useMemo(() => {
    const quarters = QUARTER_CONFIG.map(qc => {
      let totalForecast = 0;
      let totalActual = 0;
      for (const m of qc.months) {
        totalForecast += formData.months[m]?.forecast || 0;
        totalActual += formData.months[m]?.actual || 0;
      }
      const tracker = totalForecast > 0 ? Math.round((totalActual / totalForecast) * 100 * 100) / 100 : 0;
      const bonusEarned = tracker >= 100;
      return { key: qc.key, label: qc.label, totalForecast, totalActual, tracker, bonusEarned, bonusAmount: bonusEarned ? QUARTERLY_BONUS : 0 };
    });

    const annualTarget = quarters.reduce((s, q) => s + q.totalForecast, 0);
    const actualsBilled = quarters.reduce((s, q) => s + q.totalActual, 0);
    const annualTracker = annualTarget > 0 ? Math.round((actualsBilled / annualTarget) * 100 * 100) / 100 : 0;
    const quarterlyBonusTotal = quarters.reduce((s, q) => s + q.bonusAmount, 0);

    return { quarters, annualTarget, actualsBilled, annualTracker, quarterlyBonusTotal };
  }, [formData.months]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleNew = () => {
    setEditingUpn('NEW');
    setEditingRecord(null);
    setFormData({ employeeUpn: '', year: selectedYear, months: initMonths() });
  };

  const handleEdit = (record: IncentiveMonthlyData) => {
    setEditingUpn(record.employeeUpn);
    setEditingRecord(record);
    const months = initMonths();
    for (const q of record.quarters) {
      for (const m of q.months) {
        months[m.monthNum] = { forecast: m.forecast, actual: m.actual };
      }
    }
    setFormData({ employeeUpn: record.employeeUpn, year: record.year, months });
  };

  const editingHubLinked = Boolean(editingRecord?.hubLinked);

  const handleSave = async () => {
    if (editingHubLinked) {
      // Forecast/Actual for Hub-linked people are read-only (Finance Hub is the source);
      // never write Hub values back into kpi.incentive_monthly.
      showToast('Monthly figures for this person come from Finance Hub; nothing to save here.', 'info');
      setEditingUpn(null);
      setEditingRecord(null);
      return;
    }
    try {
      const payload = {
        employeeUpn: formData.employeeUpn,
        year: formData.year,
        months: Object.entries(formData.months).map(([monthNum, vals]) => ({
          monthNum: Number(monthNum),
          forecast: vals.forecast,
          actual: vals.actual,
        })),
      };
      await api.put(`/incentives/monthly/${formData.employeeUpn}?year=${formData.year}`, payload);
      showToast('Incentive data saved', 'success');
      setEditingUpn(null);
      setEditingRecord(null);
      refetch();
    } catch (err: any) {
      showToast(err?.body?.detail || 'Failed to save', 'error');
    }
  };

  const handleDelete = async (upn: string) => {
    if (!confirm('Delete all monthly data for this employee?')) return;
    try {
      await api.delete(`/incentives/monthly/${upn}?year=${selectedYear}`);
      showToast('Record deleted', 'success');
      refetch();
    } catch (err: any) {
      showToast(err?.body?.detail || 'Failed to delete', 'error');
    }
  };

  const setMonth = (monthNum: number, field: 'forecast' | 'actual', value: number) => {
    setFormData(prev => ({
      ...prev,
      months: { ...prev.months, [monthNum]: { ...prev.months[monthNum], [field]: value } },
    }));
  };

  // ---------------------------------------------------------------------------
  // Styles
  // ---------------------------------------------------------------------------
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '6px 8px', fontSize: '13px',
    border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'right',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', marginBottom: '0.25rem', fontSize: '13px', fontWeight: 600,
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      <h2>Incentive Management</h2>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
        Monthly sales targets and incentive tracking (Financial year: March - February)
      </p>

      {/* Controls */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search employees..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ flex: 1, minWidth: '250px', padding: '8px 12px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '4px' }}
        />
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #d1d5db' }}
        >
          {[2024, 2025, 2026, 2027].map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <button
          onClick={handleNew}
          style={{ padding: '8px 16px', fontSize: '14px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          + New Record
        </button>
        <span style={{ display: 'flex', alignItems: 'center', color: '#6b7280', fontSize: '14px' }}>
          {filteredRecords.length} record(s)
        </span>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Edit / New Modal                                                    */}
      {/* ------------------------------------------------------------------ */}
      {editingUpn && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', color: '#111', padding: '2rem', borderRadius: '8px', maxWidth: '850px', width: '95%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginTop: 0 }}>
              {editingUpn === 'NEW' ? 'New Incentive Record' : `Edit - ${formData.employeeUpn}`}
            </h3>

            {editingHubLinked && editingRecord && (
              <div style={{ marginBottom: '1rem', padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: 12,
                background: editingRecord.financeSource === 'hub' ? '#e3f6f3' : '#fff3e0',
                border: `1px solid ${editingRecord.financeSource === 'hub' ? '#a1e2da' : '#f0d9b1'}`,
                color: editingRecord.financeSource === 'hub' ? '#023e4c' : '#8a5a12' }}>
                {editingRecord.financeSource === 'hub'
                  ? <>From Finance Hub · as of {formatAsOf(editingRecord.hubAsOf)} · Forecast = Hub projection, Actual = Hub actual ({editingRecord.hubEntity}). Oct onward provisional. Monthly fields are read-only.</>
                  : <>Finance Hub unavailable ({editingRecord.hubError || 'no response'}) · showing stored values. Monthly fields are read-only for Hub-linked staff.</>}
              </div>
            )}

            {/* Employee selector (new only) */}
            {editingUpn === 'NEW' && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Employee</label>
                <select
                  value={formData.employeeUpn}
                  onChange={(e) => setFormData({ ...formData, employeeUpn: e.target.value })}
                  style={{ width: '100%', padding: '8px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                >
                  <option value="">Select employee...</option>
                  {employees?.map(emp => (
                    <option key={emp.EmployeeId} value={emp.Upn}>
                      {emp.DisplayName ? `${emp.DisplayName} (${emp.Upn})` : emp.Upn}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Quarter grids */}
            {QUARTER_CONFIG.map((qc, qi) => {
              const cq = computed.quarters[qi];
              return (
                <div key={qc.key} style={{ marginBottom: '1.5rem', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                  <div style={{ background: '#f3f4f6', padding: '0.5rem 1rem', fontWeight: 700, fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{qc.label}</span>
                    <span style={{ fontSize: '13px', fontWeight: 400, color: cq.bonusEarned ? '#059669' : '#6b7280' }}>
                      {cq.bonusEarned ? `Bonus earned: R 5,000` : 'Bonus: R 0'}
                    </span>
                  </div>

                  <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ padding: '0.5rem', textAlign: 'left', width: '30%' }}>Month</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right', width: '35%' }}>Forecast (R)</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right', width: '35%' }}>Actual (R)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {qc.months.map(m => (
                        <tr key={m} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '0.4rem 0.5rem', fontWeight: 500 }}>{MONTH_NAMES[m]}</td>
                          <td style={{ padding: '0.4rem 0.5rem' }}>
                            <input
                              type="number"
                              step="0.01"
                              value={formData.months[m]?.forecast || 0}
                              onChange={(e) => setMonth(m, 'forecast', Number(e.target.value))}
                              readOnly={editingHubLinked}
                              title={editingHubLinked ? 'From Finance Hub (read-only)' : undefined}
                              style={editingHubLinked ? { ...inputStyle, background: '#f3f6f7', color: '#42505a', cursor: 'not-allowed' } : inputStyle}
                            />
                          </td>
                          <td style={{ padding: '0.4rem 0.5rem' }}>
                            <input
                              type="number"
                              step="0.01"
                              value={formData.months[m]?.actual || 0}
                              onChange={(e) => setMonth(m, 'actual', Number(e.target.value))}
                              readOnly={editingHubLinked}
                              title={editingHubLinked ? 'From Finance Hub (read-only)' : undefined}
                              style={editingHubLinked ? { ...inputStyle, background: '#f3f6f7', color: '#42505a', cursor: 'not-allowed' } : inputStyle}
                            />
                          </td>
                        </tr>
                      ))}
                      {/* Quarter totals row */}
                      <tr style={{ background: '#f9fafb', fontWeight: 600, borderTop: '2px solid #e5e7eb' }}>
                        <td style={{ padding: '0.5rem' }}>
                          {qc.key} Total
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>{currencyFormat(cq.totalForecast)}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>{currencyFormat(cq.totalActual)}</td>
                      </tr>
                      <tr style={{ background: '#f9fafb' }}>
                        <td style={{ padding: '0.3rem 0.5rem', fontSize: '12px', color: '#6b7280' }}>Tracker</td>
                        <td colSpan={2} style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            background: cq.tracker >= 100 ? '#d1fae5' : cq.tracker >= 75 ? '#fef3c7' : '#fee2e2',
                            color: cq.tracker >= 100 ? '#065f46' : cq.tracker >= 75 ? '#92400e' : '#991b1b',
                          }}>
                            {cq.tracker.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })}

            {/* Annual Summary */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '1rem', marginBottom: '1rem' }}>
              <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '14px' }}>Annual Summary</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', fontSize: '13px' }}>
                <div>
                  <div style={{ color: '#6b7280', marginBottom: '0.25rem' }}>Annual Forecast</div>
                  <div style={{ fontWeight: 700, fontSize: '16px' }}>{currencyFormat(computed.annualTarget)}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', marginBottom: '0.25rem' }}>Actuals Billed</div>
                  <div style={{ fontWeight: 700, fontSize: '16px' }}>{currencyFormat(computed.actualsBilled)}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', marginBottom: '0.25rem' }}>Deficit / Surplus</div>
                  <div style={{ fontWeight: 700, fontSize: '16px', color: computed.actualsBilled - computed.annualTarget >= 0 ? '#059669' : '#dc2626' }}>
                    {computed.actualsBilled - computed.annualTarget >= 0 ? '+' : '-'}{currencyFormat(Math.abs(computed.actualsBilled - computed.annualTarget))}
                  </div>
                </div>
              </div>
              <div style={{ marginTop: '0.75rem', fontSize: '13px' }}>
                <span style={{ color: '#6b7280' }}>Annual Tracker: </span>
                <span style={{ fontWeight: 600 }}>{computed.annualTracker.toFixed(1)}%</span>
              </div>
            </div>

            {/* Bonus Summary */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '1rem', marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '14px' }}>Bonus Summary</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', fontSize: '13px' }}>
                <div>
                  <div style={{ color: '#6b7280', marginBottom: '0.25rem' }}>Quarterly Bonus</div>
                  <div style={{ fontWeight: 700, fontSize: '16px', color: '#059669' }}>
                    {currencyFormat(computed.quarterlyBonusTotal)}
                  </div>
                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                    {computed.quarters.filter(q => q.bonusEarned).length} of 4 quarters met
                  </div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', marginBottom: '0.25rem' }}>Annual Bonus</div>
                  <div style={{ fontWeight: 700, fontSize: '16px' }}>
                    {editingRecord
                      ? currencyFormat(editingRecord.annualBonusGross ?? editingRecord.annualBonus)
                      : '-'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                    {editingRecord
                      ? (editingRecord.annualBonusEarned ? 'Earned' : 'Not earned yet')
                      : 'Set in HR details'}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', marginBottom: '0.25rem' }}>KPI Bonus</div>
                  <div style={{ fontWeight: 700, fontSize: '16px' }}>R 0.00</div>
                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>TBD</div>
                </div>
              </div>
              {editingRecord && (
                <div style={{ marginTop: '0.75rem', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', fontSize: '13px' }}>
                  <div>
                    <div style={{ color: '#6b7280', marginBottom: '0.25rem' }}>SARS Tax</div>
                    <div style={{ fontWeight: 700 }}>
                      {editingRecord.annualBonusTax == null ? '-' : currencyFormat(editingRecord.annualBonusTax)}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#6b7280', marginBottom: '0.25rem' }}>Net Pay</div>
                    <div style={{ fontWeight: 700 }}>
                      {editingRecord.annualBonusNet == null ? '-' : currencyFormat(editingRecord.annualBonusNet)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', fontSize: '11px', color: '#9ca3af' }}>
                    {editingRecord.annualBonusTax == null ? 'Tax table not configured yet.' : 'Bonus tax computed on gross.'}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setEditingUpn(null);
                  setEditingRecord(null);
                }}
                style={{ padding: '8px 16px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '4px', background: 'white', color: '#333', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!formData.employeeUpn}
                title={editingHubLinked ? 'Monthly figures come from Finance Hub' : undefined}
                style={{
                  padding: '8px 16px', fontSize: '14px', background: '#3b82f6', color: 'white',
                  border: 'none', borderRadius: '4px', cursor: 'pointer',
                  opacity: !formData.employeeUpn ? 0.5 : 1,
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {hubNeedsConsent && (records || []).some(r => r.hubLinked && r.financeSource !== 'hub') && (
        <div style={{ marginBottom: '1rem', padding: '0.6rem 0.9rem', borderRadius: 6, fontSize: 13, background: '#fff3e0', border: '1px solid #f0d9b1', color: '#8a5a12', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>Finance Hub figures for Nicole and Wynand need a one-time Hub sign-in. Showing stored values meanwhile.</span>
          <button onClick={() => requestHubConsent()} style={{ padding: '4px 12px', fontSize: 12 }}>Connect Finance Hub</button>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Records list                                                        */}
      {/* ------------------------------------------------------------------ */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>Loading records...</div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
          No incentive records yet. Click "New Record" to get started.
        </div>
      ) : filteredRecords.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
          <h3>No Incentive Records</h3>
          <p>{searchQuery ? 'No records match your search' : 'Click "New Record" to add your first incentive record'}</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem' }}>Employee</th>
                <th style={{ padding: '0.75rem', textAlign: 'right' }}>Annual Forecast</th>
                <th style={{ padding: '0.75rem', textAlign: 'right' }}>Actuals</th>
                <th style={{ padding: '0.75rem', textAlign: 'right' }}>Deficit</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Q1</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Q2</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Q3</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Q4</th>
                <th style={{ padding: '0.75rem', textAlign: 'right' }}>Total Bonus</th>
                <th style={{ padding: '0.75rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((record) => {
                const deficit = record.actualsBilled - record.annualTarget;
                return (
                  <tr key={record.employeeUpn} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>
                      {record.displayName}
                      {record.hubLinked && (
                        <span
                          title={record.financeSource === 'hub'
                            ? `Forecast = Hub projection, Actual = Hub actual (${record.hubEntity}) · as of ${formatAsOf(record.hubAsOf)} · Oct onward provisional`
                            : `Finance Hub unavailable: ${record.hubError || 'no response'} · showing stored values`}
                          style={{ marginLeft: 8, padding: '1px 7px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                            background: record.financeSource === 'hub' ? '#e3f6f3' : '#fff3e0',
                            color: record.financeSource === 'hub' ? '#005a67' : '#c27415',
                            border: `1px solid ${record.financeSource === 'hub' ? '#a1e2da' : '#f0d9b1'}` }}
                        >
                          {record.financeSource === 'hub' ? `Finance Hub · ${formatAsOf(record.hubAsOf)} · Oct onward provisional` : 'Hub unavailable · stored'}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>{currencyFormat(record.annualTarget)}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>{currencyFormat(record.actualsBilled)}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', color: deficit < 0 ? '#dc2626' : '#059669' }}>
                      {deficit >= 0 ? '+' : '-'}{currencyFormat(Math.abs(deficit))}
                    </td>
                    {record.quarters.map(q => (
                      <td key={q.quarterLabel} style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block', padding: '2px 6px', borderRadius: '10px', fontSize: '12px', fontWeight: 600,
                          background: q.tracker >= 100 ? '#d1fae5' : q.tracker >= 75 ? '#fef3c7' : q.totalForecast === 0 ? '#f3f4f6' : '#fee2e2',
                          color: q.tracker >= 100 ? '#065f46' : q.tracker >= 75 ? '#92400e' : q.totalForecast === 0 ? '#9ca3af' : '#991b1b',
                        }}>
                          {q.totalForecast === 0 ? '-' : `${q.tracker.toFixed(0)}%`}
                        </span>
                      </td>
                    ))}
                    <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600, color: '#059669' }}>
                      {currencyFormat(record.totalIncentive)}
                    </td>
                    <td style={{ padding: '0.75rem', whiteSpace: 'nowrap' }}>
                      <button
                        onClick={() => handleEdit(record)}
                        style={{ padding: '4px 12px', fontSize: '13px', marginRight: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', background: 'white', color: '#333', cursor: 'pointer' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(record.employeeUpn)}
                        style={{ padding: '4px 12px', fontSize: '13px', border: '1px solid #dc2626', color: '#dc2626', borderRadius: '4px', background: 'white', cursor: 'pointer' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default IncentiveManagementPage;
